<template>
  <div class="control-panel-wrapper">
    <!-- Collapsed State - Expand Button -->
    <transition name="expand-button">
      <button
        v-if="collapsed"
        class="expand-button"
        @click="togglePanel"
        @keydown.enter="togglePanel"
        @keydown.space.prevent="togglePanel"
        aria-label="Expand panel"
        :aria-expanded="false"
      >
        <ThemeIcon icon="chevron-left" type="icons" classes="w-5 h-5" />
      </button>
    </transition>

    <!-- Expanded State - Full Control Panel -->
    <transition name="panel-slide">
      <div
        v-if="!collapsed"
        class="control-panel"
        :class="panelClasses"
        role="complementary"
        aria-label="Map controls"
      >
        <!-- Header Section -->
        <div class="panel-header">
          <div class="header-content">
            <div class="header-text">
              <h2 class="panel-title">{{ datasetFiltersMode ? 'Active Filters' : 'Map Controls' }}</h2>
              <p class="panel-subtitle">{{ datasetFiltersMode ? activeFiltersCount + ' filter' + (activeFiltersCount !== 1 ? 's' : '') + ' applied' : 'Manage layers and filters' }}</p>
            </div>
            
            <button
              class="collapse-button"
              @click="togglePanel"
              @keydown.enter="togglePanel"
              @keydown.space.prevent="togglePanel"
              aria-label="Collapse panel"
              :aria-expanded="true"
            >
              <ThemeIcon icon="chevron-right" type="icons" classes="w-5 h-5" />
            </button>
          </div>

          <!-- Location Search Integration -->
          <div v-show="!datasetFiltersMode" class="location-search-wrapper">
            <slot name="location-search"></slot>
          </div>
        </div>

        <!-- Tab Navigation (hidden in dataset filters mode) -->
        <div v-show="!datasetFiltersMode" class="tab-navigation" role="tablist" aria-label="Control panel tabs">
          <div class="tab-list">
            <button
              role="tab"
              :aria-selected="activeTab === 'layers'"
              :aria-controls="'layers-panel'"
              :tabindex="activeTab === 'layers' ? 0 : -1"
              class="tab-button"
              :class="{ 'tab-button--active': activeTab === 'layers' }"
              @click="switchTab('layers')"
              @keydown.enter="switchTab('layers')"
              @keydown.space.prevent="switchTab('layers')"
              @keydown.left="handleTabArrowKey('left')"
              @keydown.right="handleTabArrowKey('right')"
            >
              <ThemeIcon icon="layers" type="icons" classes="w-5 h-5" />
              <span>REatlas™ Layers</span>
            </button>

            <button
              v-if="hasFiltersPanel"
              role="tab"
              :aria-selected="activeTab === 'filters'"
              :aria-controls="'filters-panel'"
              :tabindex="activeTab === 'filters' ? 0 : -1"
              class="tab-button"
              :class="{ 'tab-button--active': activeTab === 'filters' }"
              @click="switchTab('filters')"
              @keydown.enter="switchTab('filters')"
              @keydown.space.prevent="switchTab('filters')"
              @keydown.left="handleTabArrowKey('left')"
              @keydown.right="handleTabArrowKey('right')"
            >
              <ThemeIcon icon="filter" type="icons" classes="w-5 h-5" />
              <span>Data Sets</span>
            </button>
          </div>
        </div>

        <!-- Tab Content Area -->
        <div class="tab-content-wrapper">
          <!-- Dataset Active Filters Mode -->
          <div
            v-show="datasetFiltersMode"
            id="dataset-filters-panel"
            role="tabpanel"
            class="tab-content"
          >
            <slot name="dataset-filters-panel"></slot>
          </div>

          <!-- Normal tabs mode — always mounted, hidden when in dataset filters mode -->
          <div v-show="!datasetFiltersMode">
            <div
              v-show="activeTab === 'layers'"
              id="layers-panel"
              role="tabpanel"
              aria-labelledby="layers-tab"
              class="tab-content"
            >
              <slot name="layers-panel"></slot>
            </div>

            <div
              v-show="activeTab === 'filters'"
              id="filters-panel"
              role="tabpanel"
              aria-labelledby="filters-tab"
              class="tab-content"
            >
              <slot name="filters-panel"></slot>
            </div>
          </div>
        </div>

        <!-- Footer Stats -->
        <div class="panel-footer">
          <div class="footer-stats">
            <div class="stat-item">
              <div class="stat-value">
                {{ activeLayersCount }}
              </div>
              <div class="stat-label">
                Active Layers
              </div>
            </div>

            <div v-if="hasFiltersPanel" class="stat-item">
              <div class="stat-value">
                {{ activeFiltersCount }}
              </div>
              <div class="stat-label">
                Active Filters
              </div>
            </div>
          </div>
        </div>


      </div>
    </transition>
  </div>
</template>

<script>
import { ANIMATION_CONFIG } from './constants.js';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ControlPanel',

  components: {
    ThemeIcon
  },

  props: {
    /**
     * Panel collapsed state
     */
    collapsed: {
      type: Boolean,
      default: false
    },

    /**
     * Number of active layers
     */
    activeLayersCount: {
      type: Number,
      default: 0
    },

    /**
     * Number of active filters
     */
    activeFiltersCount: {
      type: Number,
      default: 0
    },

    /**
     * When true, show dataset active filters instead of normal tabs
     */
    datasetFiltersMode: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      activeTab: 'layers',
      isAnimating: false
    };
  },

  computed: {
    /**
     * Dynamic classes for panel
     */
    panelClasses() {
      return {
        'control-panel--animating': this.isAnimating,
        'control-panel--mobile': this.isMobile,
        'control-panel--tablet': this.isTablet,
        'control-panel--desktop': this.isDesktop
      };
    },

    /**
     * Check if filters panel slot has content
     */
    hasFiltersPanel() {
      return !!this.$slots['filters-panel'];
    },

    /**
     * Check if mobile viewport
     */
    isMobile() {
      return window.innerWidth < 768;
    },

    /**
     * Check if tablet viewport
     */
    isTablet() {
      return window.innerWidth >= 768 && window.innerWidth < 1024;
    },

    /**
     * Check if desktop viewport
     */
    isDesktop() {
      return window.innerWidth >= 1024;
    }
  },

  methods: {
    /**
     * Toggle panel collapsed/expanded state
     */
    togglePanel() {
      this.isAnimating = true;
      const newCollapsedState = !this.collapsed;
      this.$emit('toggle', newCollapsedState);

      // Reset animating state after animation completes
      setTimeout(() => {
        this.isAnimating = false;
      }, ANIMATION_CONFIG.PANEL_SLIDE.duration);
    },

    /**
     * Switch active tab
     */
    switchTab(tab) {
      if (this.activeTab === tab) return;

      this.activeTab = tab;
      this.$emit('tab-changed', { tab });
    },

    /**
     * Handle arrow key navigation for tabs
     */
    handleTabArrowKey(direction) {
      const tabs = ['layers', 'filters'];
      const currentIndex = tabs.indexOf(this.activeTab);

      if (direction === 'left' && currentIndex > 0) {
        this.switchTab(tabs[currentIndex - 1]);
      } else if (direction === 'right' && currentIndex < tabs.length - 1) {
        this.switchTab(tabs[currentIndex + 1]);
      }
    },

    /**
     * Handle keyboard navigation
     */
    handleKeyboardNav(event) {
      // Tab key navigation is handled by browser
      // Additional custom keyboard shortcuts can be added here
    }
  }
};
</script>
